import FadeIn from '@/components/FadeIn';
import { BUSINESS } from '@/lib/constants';
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from 'lucide-react';

const JOTFORM_URL = 'https://form.jotform.com/261696649319069';

const contactInfo = [
  { icon: MapPin, label: 'Office', value: BUSINESS.address },
  { icon: Phone, label: 'Phone', value: BUSINESS.phone, href: `tel:${BUSINESS.phone}` },
  { icon: Mail, label: 'Email', value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
  { icon: Clock, label: 'Hours', value: 'Monday - Friday, 9:00 AM - 6:00 PM PST' },
];

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-wc-charcoal py-24 lg:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Contact Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.1]">
              Let's Talk About Your Business
            </h1>
            <p className="font-body text-base lg:text-lg text-white/55 mt-6 max-w-2xl leading-relaxed">
              Whether you need bookkeeping support, have questions about our services, or are ready to schedule a consultation, we're here to help.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CONTACT CONTENT */}
      <section className="py-24 lg:py-32 bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16 lg:gap-20">
            {/* INFO */}
            <div className="lg:col-span-2">
              <FadeIn>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Get in Touch</p>
                <h2 className="font-heading text-3xl font-light tracking-tight text-wc-black mb-10">
                  {BUSINESS.name}
                </h2>
                <div className="space-y-8">
                  {contactInfo.map((info, i) => (
                    <div key={i} className="flex gap-4">
                      <info.icon className="w-5 h-5 text-wc-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <p className="font-accent text-xs uppercase tracking-[0.15em] text-wc-muted mb-1">{info.label}</p>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="font-body text-sm text-wc-black hover:text-wc-gold transition-colors duration-300 break-all"
                            data-testid={`contact-info-${info.label.toLowerCase()}`}
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="font-body text-sm text-wc-black" data-testid={`contact-info-${info.label.toLowerCase()}`}>{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-12 h-px bg-wc-gold mt-10 mb-8" />
                <p className="font-body text-sm text-wc-muted leading-relaxed">
                  We respond to all inquiries within one business day. For urgent matters, please call us directly.
                </p>
              </FadeIn>
            </div>

            {/* JOTFORM EMBED */}
            <div className="lg:col-span-3">
              <FadeIn delay={150}>
                <div className="border border-wc-border" data-testid="contact-form-container">
                  <iframe
                    src={JOTFORM_URL}
                    title="Contact Form"
                    className="w-full border-0"
                    style={{ minHeight: '700px' }}
                    allowFullScreen
                    data-testid="contact-jotform"
                  />
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION BANNER */}
      <section className="py-20 bg-wc-bg-alt border-t border-wc-border" data-testid="contact-location">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-2">Beverly Hills Office</p>
              <p className="font-heading text-2xl font-light text-wc-black">{BUSINESS.address}</p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
